// NFCManager.js
import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Alert, Button } from "react-bootstrap";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { logEvent } from "./Analytics";
import NFCPlayerView from "./NFCPlayerView";

/* global NDEFReader */

const NFCManager = () => {
    const [message, setMessage] = useState("Esperando interacción NFC...");
    const [error, setError] = useState(null);
    const [songs, setSongs] = useState([]);
    const [selectedSong, setSelectedSong] = useState(null);

    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, "songs"),
            (snapshot) => {
                const songList = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setSongs(songList);
                if (!selectedSong && songList.length > 0) {
                    setSelectedSong(songList[0]);
                }
            },
            (error) => {
                setError("Error al obtener datos en tiempo real.");
                console.error("Error al escuchar canciones:", error);
            }
        );

        return () => unsubscribe();
    }, [selectedSong]);

    const handleNFCReading = async () => {
        if (!("NDEFReader" in window)) {
            setError("La API Web NFC no está soportada en este navegador.");
            return;
        }

        try {
            const ndef = new NDEFReader();
            setMessage("Escaneo NFC iniciado...");
            await ndef.scan();

            ndef.addEventListener("readingerror", () => {
                setError("Error al leer el chip NFC.");
                logEvent("NFC Usage", "Read NFC Error", "Error while reading NFC chip");
            });

            ndef.addEventListener("reading", ({ message }) => {
                const records = message.records.map((record) => ({
                    type: record.recordType,
                    data: record.data || "Datos no disponibles",
                }));

                const urlRecord = records.find((record) =>
                    record.type === "url" || (record.type === "text" && record.data.startsWith("http"))
                );

                if (urlRecord) {
                    const url = urlRecord.data;
                    const matchedSong = songs.find((song) => song.url === url);

                    if (matchedSong) {
                        setSelectedSong(matchedSong);
                        setMessage("Canción detectada desde el chip NFC:");
                        logEvent("NFC Usage", "Read NFC Success", `Matched Song: ${matchedSong.title}`);
                    } else {
                        setMessage("Enlace detectado, pero no coincide con ninguna canción.");
                        logEvent("NFC Usage", "Read NFC Success", `Unmatched URL: ${url}`);
                    }
                } else {
                    setMessage("No se encontró un enlace válido en el chip NFC.");
                    logEvent("NFC Usage", "Read NFC", "No valid link found");
                }
            });
        } catch (err) {
            setError(`Error durante la lectura NFC: ${err.message}`);
            logEvent("NFC Usage", "Read NFC Error", `Error: ${err.message}`);
        }
    };

    const handleNFCWriting = async () => {
        if (!("NDEFReader" in window)) {
            setError("La API Web NFC no está soportada en este navegador.");
            return;
        }

        if (!selectedSong) {
            setError("No hay datos disponibles para escribir en el NFC.");
            return;
        }

        const data = {
            records: [
                { recordType: "text", data: `Título: ${selectedSong.title}` },
                { recordType: "text", data: `Artista: ${selectedSong.artist}` },
                { recordType: "url", data: selectedSong.url },
            ],
        };

        try {
            const ndef = new NDEFReader();
            await ndef.write(data);
            setMessage("Datos escritos correctamente en el chip NFC.");
            logEvent("NFC Usage", "Write NFC Success", `Successfully wrote NFC: ${selectedSong.title}`);
        } catch (error) {
            setError(`Error al escribir en el NFC: ${error.name} - ${error.message}`);
            logEvent("NFC Usage", "Write NFC Error", `Error: ${error.message}`);
        }
    };

    return (
        <div className="container text-center mt-4">
            <h1>Administrador NFC</h1>
            <div className="mt-3">
                <h5>Canción seleccionada para escritura NFC:</h5>
                {selectedSong ? (
                    <div>
                        <p>
                            <strong>{selectedSong.title}</strong> por {selectedSong.artist}
                        </p>
                    </div>
                ) : (
                    <p>No hay canciones disponibles.</p>
                )}
                <select
                    className="form-select mt-2"
                    onChange={(e) =>
                        setSelectedSong(songs.find((song) => song.id === e.target.value))
                    }
                >
                    {songs.map((song) => (
                        <option key={song.id} value={song.id}>
                            {song.title} - {song.artist}
                        </option>
                    ))}
                </select>
            </div>
            <div className="mt-3">
                <Button onClick={handleNFCReading} variant="primary" className="me-2">
                    <i className="bi bi-search"></i> Leer NFC
                </Button>
                <Button onClick={handleNFCWriting} variant="success" className="me-2">
                    <i className="bi bi-pencil-square"></i> Escribir NFC
                </Button>
            </div>
            <div className="mt-4">
                {message && (
                    <Alert variant="info" className="text-start">
                        <i className="bi bi-info-circle"></i> {message}
                    </Alert>
                )}
                {error && (
                    <Alert variant="danger" className="text-start">
                        <i className="bi bi-exclamation-triangle-fill"></i> {error}
                    </Alert>
                )}
            </div>
            <div className="mt-4">
                {selectedSong && <NFCPlayerView song={selectedSong} />}
            </div>
        </div>
    );
};

export default NFCManager;
